<template>
  <b-modal
    v-model="modalShow"
    :title="editingId ? 'Редактировать правило' : 'Новое правило доступности'"
    size="xl"
    no-close-on-backdrop
    hide-footer
  >
    <div class="raw-wrap">
      <!-- Step indicator -->
      <div class="raw-steps">
        <div
          v-for="(s, i) in steps"
          :key="i"
          class="raw-step"
          :class="{
            'raw-step--active': stepIdx === i,
            'raw-step--done': stepIdx > i,
          }"
          @click="goStep(i)"
        >
          <span class="raw-step-num">{{ i + 1 }}</span>
          <span class="raw-step-name">{{ s }}</span>
        </div>
      </div>

      <!-- STEP 1: Контекст -->
      <div
        v-show="stepIdx === 0"
        class="raw-step-body"
      >
        <h5>Кому и где применить</h5>
        <div class="raw-row">
          <label>Роль <span class="raw-req">*</span></label>
          <div class="raw-picker-wrap">
            <cbs-inline-picker
              :options="roleOptions"
              :display-val="displayRole"
              label-key="title"
              value-key="value"
              placeholder="Поиск роли…"
              empty-label="— любая роль —"
              @select="opt => form.roleid = opt.value"
            />
            <button
              v-if="form.roleid != null"
              class="raw-picker-clear"
              title="Очистить"
              @click="form.roleid = null"
            >
              ×
            </button>
          </div>
          <div class="raw-hint">
            Кому применять. «Любая» = для всех пользователей.
          </div>
        </div>
        <div class="raw-row">
          <label>Объект применения</label>
          <div class="raw-picker-wrap">
            <cbs-inline-picker
              :options="objectOptions"
              :display-val="displayObject"
              label-key="title"
              value-key="value"
              placeholder="Поиск формы/куба…"
              empty-label="— любой —"
              @select="opt => form.objectid = opt.value"
            />
            <button
              v-if="form.objectid != null"
              class="raw-picker-clear"
              title="Очистить"
              @click="form.objectid = null"
            >
              ×
            </button>
          </div>
          <div class="raw-hint">
            Где применять (конкретная форма/куб). «Любой» = везде.
          </div>
        </div>
        <div class="raw-row">
          <label>Поле-ссылка на этот справочник</label>
          <div class="raw-picker-wrap">
            <cbs-inline-picker
              :options="targetEntityOptions"
              :display-val="displayEntity"
              label-key="title"
              value-key="value"
              placeholder="Поиск поля…"
              empty-label="— автоматически —"
              @select="opt => form.entityid = opt.value"
            />
            <button
              v-if="form.entityid != null"
              class="raw-picker-clear"
              title="Очистить"
              @click="form.entityid = null"
            >
              ×
            </button>
          </div>
          <div
            v-if="catalogTargetEntities.length === 0"
            class="raw-warn"
          >
            ⚠ На этот справочник нет ref-полей. Добавьте поле-ссылку в любой объект — тогда правила можно будет создавать.
          </div>
        </div>
      </div>

      <!-- STEP 2: Тип -->
      <div
        v-show="stepIdx === 1"
        class="raw-step-body"
      >
        <h5>Какие записи разрешить?</h5>
        <div class="raw-types">
          <label
            class="raw-type"
            :class="{ 'raw-type--active': form.ruleType === 'records' }"
          >
            <input
              v-model="form.ruleType"
              type="radio"
              value="records"
            >
            <div>
              <strong>Список записей</strong>
              <div class="raw-type-hint">Вручную выбрать конкретные записи каталога.</div>
            </div>
          </label>
          <label
            class="raw-type"
            :class="{ 'raw-type--active': form.ruleType === 'field' }"
          >
            <input
              v-model="form.ruleType"
              type="radio"
              value="field"
            >
            <div>
              <strong>По полю каталога</strong>
              <div class="raw-type-hint">Фильтр по значению поля каталога (например code = 02 или parent = X).</div>
            </div>
          </label>
          <label
            class="raw-type"
            :class="{ 'raw-type--active': form.ruleType === 'paramentity', 'raw-type--disabled': catalogRefAttributes.length === 0 }"
          >
            <input
              v-model="form.ruleType"
              type="radio"
              value="paramentity"
              :disabled="catalogRefAttributes.length === 0"
            >
            <div>
              <strong>По выбранному значению (paramentity)</strong>
              <div class="raw-type-hint">
                Привязать к параметру формы (выбранное подразделение/сценарий и т.п.).
                <span
                  v-if="catalogRefAttributes.length === 0"
                  class="raw-warn-inline"
                >
                  Недоступно — у каталога нет ref-полей.
                </span>
              </div>
            </div>
          </label>
          <label
            class="raw-type"
            :class="{ 'raw-type--active': form.ruleType === 'per_context', 'raw-type--disabled': contextCatalogs.length === 0 }"
          >
            <input
              v-model="form.ruleType"
              type="radio"
              value="per_context"
              :disabled="contextCatalogs.length === 0"
            >
            <div>
              <strong>Индивидуально по контексту</strong>
              <div class="raw-type-hint">
                Разные записи для каждого контекста (подразделения/школы и т.п.).
                <span
                  v-if="contextCatalogs.length === 0"
                  class="raw-warn-inline"
                >
                  Недоступно — нет контекстных справочников.
                </span>
              </div>
            </div>
          </label>
        </div>
      </div>

      <!-- STEP 3: Подробности -->
      <div
        v-show="stepIdx === 2"
        class="raw-step-body"
      >
        <h5>Настройка</h5>

        <!-- Type: records -->
        <div v-if="form.ruleType === 'records'">
          <div class="raw-row">
            <input
              v-model="recordsSearch"
              placeholder="🔍 Поиск по имени или коду…"
              class="raw-input"
            >
          </div>
          <div class="raw-row raw-toolbar">
            <button
              class="raw-link"
              @click="selectAllVisible"
            >
              Выбрать всех видимых
            </button>
            <button
              class="raw-link"
              @click="clearSelection"
            >
              Снять выделение
            </button>
            <button
              class="raw-link raw-link--secondary"
              @click="toggleExpandAll"
            >
              {{ allExpanded ? 'Свернуть всё' : 'Развернуть всё' }}
            </button>
            <span class="raw-counter">Выбрано: {{ form.selectedRecordIds.length }}</span>
            <button
              class="raw-ai-btn"
              :disabled="aiBusy"
              @click="aiFindRecords"
            >
              🤖 Найти по описанию
            </button>
          </div>
          <div
            v-if="aiSuggestion"
            class="raw-ai-suggestion"
          >
            <span>{{ aiSuggestion.text }}</span>
            <button
              class="raw-link"
              @click="applyAiSuggestion"
            >
              Применить выбор
            </button>
            <button
              class="raw-link raw-link--secondary"
              @click="aiSuggestion = null"
            >
              Отклонить
            </button>
          </div>
          <div class="raw-records-list">
            <template v-if="filteredTreeRoots.length > 0">
              <ref-avail-tree-node
                v-for="node in filteredTreeRoots"
                :key="node.id"
                :node="node"
                :selected-set="selectedRecordSet"
                :expanded-set="expandedSet"
                :search-active="!!recordsSearch.trim()"
                @toggle="onTreeToggleSelect"
                @expand="onTreeToggleExpand"
              />
            </template>
            <div
              v-else
              class="raw-empty"
            >
              Ничего не найдено
            </div>
          </div>
        </div>

        <!-- Type: field -->
        <div v-if="form.ruleType === 'field'">
          <div class="raw-row">
            <label>Поле каталога <span class="raw-req">*</span></label>
            <cbs-inline-picker
              :options="catalogAttrOptions"
              :display-val="displayCatalogAttr"
              label-key="title"
              value-key="value"
              placeholder="Поиск поля…"
              empty-label="— выберите —"
              @select="opt => form.fieldSid = opt.value"
            />
          </div>
          <div class="raw-row">
            <label>Оператор</label>
            <select
              v-model="form.fieldOper"
              class="raw-input"
            >
              <option value="equal">
                равно
              </option>
              <option value="notequal">
                не равно
              </option>
              <option value="like">
                содержит / начинается с
              </option>
              <option value="in">
                в списке
              </option>
            </select>
          </div>
          <div class="raw-row">
            <label>Значение <span class="raw-req">*</span></label>
            <input
              v-model="form.fieldValue"
              class="raw-input"
              :placeholder="form.fieldOper === 'in' ? 'через запятую: 1, 2, 3' : 'значение'"
            >
          </div>
        </div>

        <!-- Type: paramentity -->
        <div v-if="form.ruleType === 'paramentity'">
          <div class="raw-row">
            <label>Поле каталога (ref) <span class="raw-req">*</span></label>
            <cbs-inline-picker
              :options="catalogRefAttrOptions"
              :display-val="displayCatalogRefAttr"
              label-key="title"
              value-key="value"
              placeholder="Поиск ref-поля…"
              empty-label="— выберите —"
              @select="opt => form.paramFieldEntityId = opt.value"
            />
            <div class="raw-hint">
              Поле каталога которое должно совпадать с параметром формы.
            </div>
          </div>
          <div class="raw-row">
            <label>Параметр формы (paramentity) <span class="raw-req">*</span></label>
            <div class="raw-picker-wrap">
              <cbs-inline-picker
                :options="paramentityOptions"
                :display-val="displayParamentity"
                label-key="title"
                value-key="value"
                placeholder="Поиск параметра…"
                empty-label="— авто (по полю выше) —"
                @select="opt => form.paramSourceEntityId = opt.value"
              />
              <button
                v-if="form.paramSourceEntityId != null"
                class="raw-picker-clear"
                title="Очистить"
                @click="form.paramSourceEntityId = null"
              >
                ×
              </button>
            </div>
            <div class="raw-hint">
              Если оставить «авто» — будет использовано то же поле что слева.
            </div>
          </div>
        </div>

        <!-- Type: per_context -->
        <div v-if="form.ruleType === 'per_context'">
          <div class="raw-row">
            <label>Контекст-каталог <span class="raw-req">*</span></label>
            <cbs-inline-picker
              :options="contextCatalogOptions"
              :display-val="displayContextCatalog"
              label-key="title"
              value-key="value"
              placeholder="Поиск каталога…"
              empty-label="— выберите —"
              @select="opt => form.contextCatalogId = opt.value"
            />
          </div>

          <div
            v-if="form.contextCatalogId"
            class="raw-context-mapping"
          >
            <div class="raw-row raw-toolbar">
              <span class="raw-counter">Заполнено: {{ filledContextCount }}/{{ selectedContext.records.length }}</span>
              <button
                class="raw-ai-btn"
                :disabled="aiBusy"
                @click="aiSuggestDistribution"
              >
                🤖 Подсказать распределение
              </button>
            </div>
            <div
              v-if="aiSuggestion"
              class="raw-ai-suggestion"
            >
              <span>{{ aiSuggestion.text }}</span>
              <button
                class="raw-link"
                @click="applyAiSuggestion"
              >
                Применить
              </button>
              <button
                class="raw-link raw-link--secondary"
                @click="aiSuggestion = null"
              >
                Отклонить
              </button>
            </div>

            <div class="raw-md-layout">
              <!-- LEFT: subdivisions list with search -->
              <div class="raw-md-master">
                <input
                  v-model="contextSearch"
                  placeholder="🔍 Поиск подразделения…"
                  class="raw-input raw-input--small"
                >
                <div class="raw-md-list">
                  <div
                    v-for="ctxRec in filteredContextRecords"
                    :key="ctxRec.id"
                    class="raw-md-item"
                    :class="{
                      'raw-md-item--active': Number(activeCtxId) === Number(ctxRec.id),
                      'raw-md-item--filled': (form.contextMapping[ctxRec.id] || []).length > 0,
                    }"
                    @click.stop="pickCtx(ctxRec.id)"
                  >
                    <span class="raw-md-name">{{ ctxRec.name }}</span>
                    <span
                      v-if="(form.contextMapping[ctxRec.id] || []).length > 0"
                      class="raw-md-badge"
                    >{{ (form.contextMapping[ctxRec.id] || []).length }}</span>
                  </div>
                  <div
                    v-if="filteredContextRecords.length === 0"
                    class="raw-empty"
                  >
                    Ничего не найдено
                  </div>
                </div>
              </div>

              <!-- RIGHT: articles for active subdivision -->
              <div class="raw-md-detail">
                <div
                  v-if="!activeCtxId"
                  class="raw-md-placeholder"
                >
                  ← Выберите подразделение слева
                </div>
                <div v-else>
                  <div class="raw-md-detail-head">
                    <strong>{{ activeCtxName }}</strong>
                    <span class="raw-counter">
                      выбрано: {{ (form.contextMapping[activeCtxId] || []).length }}
                    </span>
                  </div>
                  <input
                    v-model="recordsSearch"
                    placeholder="🔍 Поиск статьи (имя или код)…"
                    class="raw-input raw-input--small"
                  >
                  <div class="raw-row raw-toolbar">
                    <button
                      class="raw-link"
                      @click="ctxSelectAllVisible"
                    >
                      Выбрать видимых
                    </button>
                    <button
                      class="raw-link"
                      @click="ctxClearActive"
                    >
                      Снять выделение
                    </button>
                    <button
                      class="raw-link raw-link--secondary"
                      @click="toggleExpandAll"
                    >
                      {{ allExpanded ? 'Свернуть всё' : 'Развернуть всё' }}
                    </button>
                    <button
                      class="raw-link raw-link--secondary"
                      @click="copyFromOther"
                    >
                      Копировать из…
                    </button>
                  </div>
                  <div class="raw-records-list raw-records-list--md">
                    <template v-if="filteredTreeRoots.length > 0">
                      <ref-avail-tree-node
                        v-for="node in filteredTreeRoots"
                        :key="node.id"
                        :node="node"
                        :selected-set="activeCtxSelectedSet"
                        :expanded-set="expandedSet"
                        :search-active="!!recordsSearch.trim()"
                        @toggle="onCtxTreeToggleSelect"
                        @expand="onTreeToggleExpand"
                      />
                    </template>
                    <div
                      v-else
                      class="raw-empty"
                    >
                      Ничего не найдено
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- STEP 4: Preview & Save -->
      <div
        v-show="stepIdx === 3"
        class="raw-step-body"
      >
        <h5>Превью правила</h5>
        <div class="raw-preview">
          <div><strong>Роль:</strong> {{ roleName }}</div>
          <div><strong>Объект:</strong> {{ objectName }}</div>
          <div><strong>Поле-ссылка:</strong> {{ entityName }}</div>
          <div><strong>Тип:</strong> {{ typeLabel }}</div>
          <div><strong>Описание:</strong> {{ humanFilter }}</div>
        </div>
        <details class="raw-json-details">
          <summary>JSON-фильтр (для разработчиков)</summary>
          <pre class="raw-json">{{ generatedJsonFilterPretty }}</pre>
        </details>
        <div
          v-if="form.ruleType === 'per_context' && perContextRulesCount > 1"
          class="raw-info"
        >
          ℹ️ Будет создано {{ perContextRulesCount }} правил (по одному на каждый заполненный контекст).
        </div>
      </div>

      <!-- Footer -->
      <div class="raw-footer">
        <button
          class="raw-btn"
          @click="cancel"
        >
          Отмена
        </button>
        <button
          v-if="stepIdx > 0"
          class="raw-btn"
          @click="prevStep"
        >
          ← Назад
        </button>
        <button
          v-if="stepIdx < steps.length - 1"
          class="raw-btn raw-btn--primary"
          :disabled="!canNext"
          @click="nextStep"
        >
          Далее →
        </button>
        <button
          v-if="stepIdx === steps.length - 1"
          class="raw-btn raw-btn--primary"
          :disabled="!canSave || saving"
          @click="save"
        >
          {{ saving ? 'Сохранение…' : 'Сохранить' }}
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { BModal } from 'bootstrap-vue'
import { getSessionToken } from '@/cubus/jwt/tokenHolder'
import CbsInlinePicker from '@/cubus/components/inline-picker/CbsInlinePicker.vue'

function getBaseURL() {
  try {
    const cfg = require('@/libs/config.json')
    return cfg.baseURL || 'http://localhost:9000'
  } catch (e) { return 'http://localhost:9000' }
}

const RefAvailTreeNode = {
  name: 'RefAvailTreeNode',
  props: {
    node: { type: Object, required: true },
    selectedSet: { type: null, required: true }, // Set
    expandedSet: { type: null, required: true }, // Set
    searchActive: { type: Boolean, default: false },
    depth: { type: Number, default: 0 },
  },
  computed: {
    isFolder() { return this.node.isfolder || (this.node.children && this.node.children.length > 0) },
    isExpanded() { return this.searchActive || this.expandedSet.has(this.node.id) },
    descendantLeafIds() { return this.node._descendantIds || [] },
    selectedCount() {
      if (!this.isFolder) return this.selectedSet.has(this.node.id) ? 1 : 0
      let sel = 0
      for (const id of this.descendantLeafIds) if (this.selectedSet.has(id)) sel += 1
      return sel
    },
    totalLeaves() {
      if (!this.isFolder) return 1
      return this.descendantLeafIds.length || 0
    },
    selectionState() {
      if (this.totalLeaves === 0) return this.selectedSet.has(this.node.id) ? 'full' : 'none'
      if (this.selectedCount === 0) return 'none'
      if (this.selectedCount === this.totalLeaves) return 'full'
      return 'partial'
    },
  },
  methods: {
    onClickRow() {
      // Click on row: for folders → expand (intuitive). Use checkbox to select.
      if (this.isFolder) this.$emit('expand', this.node.id)
      else this.$emit('toggle', { node: this.node, mode: 'leaf' })
    },
    onClickArrow(e) {
      e.stopPropagation()
      this.$emit('expand', this.node.id)
    },
    onCheckboxChange(e) {
      e.stopPropagation()
      this.$emit('toggle', { node: this.node, mode: this.isFolder ? 'cascade' : 'leaf' })
    },
  },
  render(h) {
    const state = this.selectionState
    const rowClass = ['raw-tree-row']
    if (state === 'full') rowClass.push('raw-tree-row--checked')
    if (state === 'partial') rowClass.push('raw-tree-row--partial')
    if (this.isFolder) rowClass.push('raw-tree-row--folder')

    // All rows share the same column layout — indent + chevron-slot + checkbox + code + name + badge
    const cells = []

    // Single indent block (fixed width per depth)
    cells.push(h('span', {
      class: 'raw-tree-indent',
      style: { width: `${this.depth * 14}px` },
    }))

    // Chevron — SVG ChevronRight (rotates 90deg on expand), same size for leaves
    if (this.isFolder) {
      cells.push(h('span', {
        class: ['raw-tree-chev', this.isExpanded ? 'raw-tree-chev--open' : ''],
        on: { click: this.onClickArrow },
      }, [h('svg', {
        attrs: { width: 14, height: 14, viewBox: '0 0 24 24' },
      }, [h('polyline', {
        attrs: {
          points: '9 18 15 12 9 6',
          fill: 'none',
          stroke: 'currentColor',
          'stroke-width': 2,
          'stroke-linecap': 'round',
          'stroke-linejoin': 'round',
        },
      })])]))
    } else {
      cells.push(h('span', { class: 'raw-tree-chev raw-tree-chev--empty' }))
    }

    // Checkbox (always same size)
    cells.push(h('label', { class: 'raw-tree-cbx', on: { click: e => e.stopPropagation() } }, [
      h('input', {
        attrs: { type: 'checkbox' },
        domProps: { checked: state === 'full', indeterminate: state === 'partial' },
        on: { change: this.onCheckboxChange },
      }),
      h('span', { class: 'raw-tree-cbx-mark' }),
    ]))

    // Code chip (always rendered, even empty — keeps alignment)
    cells.push(h('span', { class: 'raw-tree-code' }, this.node.code || ''))

    // Name
    cells.push(h('span', { class: 'raw-tree-name' }, this.node.name))

    // Right-side counter (for folders only)
    if (this.isFolder && this.totalLeaves > 0) {
      cells.push(h('span', {
        class: ['raw-tree-badge', state === 'full' ? 'raw-tree-badge--full' : (state === 'partial' ? 'raw-tree-badge--partial' : '')],
      }, `${this.selectedCount}/${this.totalLeaves}`))
    }

    const rowVnode = h('div', { class: rowClass, on: { click: this.onClickRow } }, cells)
    const out = [rowVnode]
    if (this.isFolder && this.isExpanded && this.node.children && this.node.children.length > 0) {
      const kids = this.node.children.map(child => h('ref-avail-tree-node', {
        key: child.id,
        props: {
          node: child,
          selectedSet: this.selectedSet,
          expandedSet: this.expandedSet,
          searchActive: this.searchActive,
          depth: this.depth + 1,
        },
        on: {
          toggle: payload => this.$emit('toggle', payload),
          expand: id => this.$emit('expand', id),
        },
      }))
      out.push(h('div', { class: 'raw-tree-children' }, kids))
    }
    return h('div', { class: 'raw-tree-node' }, out)
  },
}

export default {
  name: 'CbsRefAvailabilityWizard',
  components: { BModal, RefAvailTreeNode, CbsInlinePicker },
  props: {
    show: { type: Boolean, default: false },
    catalog: { type: Object, default: null },
    catalogRecords: { type: Array, default: () => [] },
    catalogTargetEntities: { type: Array, default: () => [] }, // [{id,sid,name}] — entities pointing to this catalog
    catalogRefAttributes: { type: Array, default: () => [] }, // ref-attrs of this catalog
    contextCatalogs: { type: Array, default: () => [] }, // [{id,sid,name,records:[]}]
    refEntities: { type: Array, default: () => [] }, // all ref entities
    roles: { type: Array, default: () => [] },
    objects: { type: Array, default: () => [] },
    initialRule: { type: Object, default: null }, // when editing — pre-fill
  },
  data() {
    return {
      stepIdx: 0,
      steps: ['Контекст', 'Тип', 'Настройка', 'Превью'],
      saving: false,
      aiBusy: false,
      aiSuggestion: null, // { text, ids?, mapping? }
      recordsSearch: '',
      contextSearch: '',
      activeCtxId: null,
      expandedSet: new Set(),
      form: this.makeBlankForm(),
      editingId: null,
    }
  },
  computed: {
    modalShow: {
      get() { return this.show },
      set(v) { if (!v) this.$emit('close') },
    },
    catalogId() { return this.catalog && this.catalog.id },
    catalogAttrs() { return (this.catalog && this.catalog.attributes) || [] },
    selectedContext() {
      return this.contextCatalogs.find(c => c.id === this.form.contextCatalogId) || { records: [] }
    },
    filteredRecords() {
      const q = (this.recordsSearch || '').toLowerCase().trim()
      if (!q) return this.catalogRecords.slice(0, 500)
      return this.catalogRecords.filter(r => {
        const code = String(r.code || '').toLowerCase()
        const name = String(r.name || '').toLowerCase()
        return code.includes(q) || name.includes(q)
      }).slice(0, 500)
    },
    treeNodes() {
      // Build tree { id, code, name, isfolder, parentid, children[], _descendantIds[] }
      const byId = new Map()
      const list = this.catalogRecords.map(r => ({
        ...r,
        children: [],
        _descendantIds: [],
      }))
      list.forEach(n => byId.set(n.id, n))
      const roots = []
      list.forEach(n => {
        const p = n.parentid && byId.get(n.parentid)
        if (p) p.children.push(n)
        else roots.push(n)
      })
      // Sort each level by code then name
      const sortFn = (a, b) => {
        const ac = String(a.code || ''); const
          bc = String(b.code || '')
        if (ac && bc && ac !== bc) return ac.localeCompare(bc, 'ru', { numeric: true })
        return String(a.name || '').localeCompare(String(b.name || ''), 'ru')
      }
      const visit = node => {
        node.children.sort(sortFn)
        const ids = []
        node.children.forEach(c => {
          visit(c)
          if (!c.isfolder) ids.push(c.id)
          ids.push(...c._descendantIds)
        })
        node._descendantIds = ids
      }
      roots.sort(sortFn)
      roots.forEach(visit)
      return { roots, byId, list }
    },
    filteredTreeRoots() {
      const q = (this.recordsSearch || '').toLowerCase().trim()
      const { roots } = this.treeNodes
      if (!q) return roots
      // Filter: keep nodes (and their ancestors) where any descendant matches
      const matches = id => {
        const n = this.treeNodes.byId.get(id)
        if (!n) return false
        const code = String(n.code || '').toLowerCase()
        const name = String(n.name || '').toLowerCase()
        return code.includes(q) || name.includes(q)
      }
      const cloneIfHas = node => {
        const selfMatch = matches(node.id)
        const childResults = node.children.map(cloneIfHas).filter(Boolean)
        if (selfMatch || childResults.length > 0) {
          return { ...node, children: childResults }
        }
        return null
      }
      return roots.map(cloneIfHas).filter(Boolean)
    },
    selectedRecordSet() {
      return new Set(this.form.selectedRecordIds)
    },
    activeCtxSelectedSet() {
      if (!this.activeCtxId) return new Set()
      return new Set(this.form.contextMapping[this.activeCtxId] || [])
    },
    allExpanded() {
      const folderIds = this.treeNodes.list.filter(n => n.isfolder || n.children.length > 0).map(n => n.id)
      if (folderIds.length === 0) return false
      return folderIds.every(id => this.expandedSet.has(id))
    },
    roleOptions() {
      return this.roles.map(r => ({ value: r.id, title: r.name }))
    },
    objectOptions() {
      return this.objects.map(o => ({ value: o.id, title: `${o.name} (#${o.id})` }))
    },
    targetEntityOptions() {
      return this.catalogTargetEntities.map(e => ({ value: e.id, title: `${e.name} (${e.sid || `entity #${e.id}`})` }))
    },
    catalogAttrOptions() {
      return (this.catalogAttrs || []).map(a => ({ value: a.sid, title: `${a.name} (${a.sid})` }))
    },
    catalogRefAttrOptions() {
      return (this.catalogRefAttributes || []).map(a => ({
        value: a.entityid,
        title: `${a.name} (${a.sid}) → obj #${a.refobjectid}`,
      }))
    },
    paramentityOptions() {
      return this.paramentityCandidates.map(e => ({ value: e.id, title: `${e.name} (${e.sid})` }))
    },
    contextCatalogOptions() {
      return this.contextCatalogs.map(c => ({
        value: c.id,
        title: `${c.name} · ${(c.records || []).length} записей`,
      }))
    },
    // displayVal computeds — find the title for currently selected value
    displayRole() { const o = this.roleOptions.find(x => x.value === this.form.roleid); return o ? o.title : '' },
    displayObject() { const o = this.objectOptions.find(x => x.value === this.form.objectid); return o ? o.title : '' },
    displayEntity() { const o = this.targetEntityOptions.find(x => x.value === this.form.entityid); return o ? o.title : '' },
    displayCatalogAttr() { const o = this.catalogAttrOptions.find(x => x.value === this.form.fieldSid); return o ? o.title : '' },
    displayCatalogRefAttr() { const o = this.catalogRefAttrOptions.find(x => x.value === this.form.paramFieldEntityId); return o ? o.title : '' },
    displayParamentity() { const o = this.paramentityOptions.find(x => x.value === this.form.paramSourceEntityId); return o ? o.title : '' },
    displayContextCatalog() { const o = this.contextCatalogOptions.find(x => x.value === this.form.contextCatalogId); return o ? o.title : '' },
    paramentityCandidates() {
      // ref-entities whose refobjectid = refobjectid of selected paramFieldEntity (matching target)
      if (!this.form.paramFieldEntityId) return this.refEntities
      const attr = this.catalogRefAttributes.find(a => a.entityid === this.form.paramFieldEntityId)
      if (!attr) return this.refEntities
      return this.refEntities.filter(e => Number(e.refobjectid) === Number(attr.refobjectid))
    },
    filledContextCount() {
      return Object.keys(this.form.contextMapping).filter(k => (this.form.contextMapping[k] || []).length > 0).length
    },
    filteredContextRecords() {
      const q = (this.contextSearch || '').toLowerCase().trim()
      const recs = this.selectedContext.records || []
      if (!q) return recs
      return recs.filter(r => {
        const code = String(r.code || '').toLowerCase()
        const name = String(r.name || '').toLowerCase()
        return code.includes(q) || name.includes(q)
      })
    },
    activeCtxName() {
      const rec = (this.selectedContext.records || []).find(r => r.id === this.activeCtxId)
      return rec ? rec.name : ''
    },
    perContextRulesCount() {
      return this.filledContextCount
    },
    canNext() {
      if (this.stepIdx === 0) return this.form.entityid || this.catalogTargetEntities.length > 0
      if (this.stepIdx === 1) return !!this.form.ruleType
      if (this.stepIdx === 2) {
        if (this.form.ruleType === 'records') return this.form.selectedRecordIds.length > 0
        if (this.form.ruleType === 'field') return this.form.fieldSid && this.form.fieldValue
        if (this.form.ruleType === 'paramentity') return !!this.form.paramFieldEntityId
        if (this.form.ruleType === 'per_context') return this.filledContextCount > 0
      }
      return true
    },
    canSave() { return this.canNext },
    roleName() {
      const r = this.roles.find(x => x.id === this.form.roleid)
      return r ? r.name : '— любая —'
    },
    objectName() {
      const o = this.objects.find(x => x.id === this.form.objectid)
      return o ? o.name : '— любой —'
    },
    entityName() {
      const eid = this.form.entityid || (this.catalogTargetEntities[0] && this.catalogTargetEntities[0].id)
      const e = this.catalogTargetEntities.find(x => x.id === eid)
      return e ? `${e.name} (${e.sid})` : '— не задано —'
    },
    typeLabel() {
      switch (this.form.ruleType) {
        case 'records': return `Список из ${this.form.selectedRecordIds.length} записей`
        case 'field': return `Фильтр по полю «${this.form.fieldSid}» ${this.form.fieldOper} «${this.form.fieldValue}»`
        case 'paramentity': return 'По выбранному значению (paramentity)'
        case 'per_context': return `Индивидуально по «${this.selectedContext.name || '?'}»`
        default: return '—'
      }
    },
    humanFilter() {
      if (this.form.ruleType === 'records') {
        const cnt = this.form.selectedRecordIds.length
        return cnt > 0 ? `Только эти ${cnt} записей` : 'Не выбрано'
      }
      if (this.form.ruleType === 'field') return `${this.form.fieldSid} ${this.form.fieldOper} ${this.form.fieldValue}`
      if (this.form.ruleType === 'paramentity') {
        const attr = this.catalogRefAttributes.find(a => a.entityid === this.form.paramFieldEntityId)
        const src = this.refEntities.find(e => e.id === this.form.paramSourceEntityId)
        return `${attr?.sid || '?'} = paramentity(${src?.sid || 'auto'})`
      }
      if (this.form.ruleType === 'per_context') return `${this.filledContextCount} правил по контексту`
      return '—'
    },
    generatedRules() {
      // Returns array of rules to save (usually 1, multiple for per_context)
      const baseEntityId = this.form.entityid || (this.catalogTargetEntities[0] && this.catalogTargetEntities[0].id) || null
      if (!baseEntityId) return []

      if (this.form.ruleType === 'records') {
        return [{
          entityid: baseEntityId,
          objectid: this.form.objectid,
          roleid: this.form.roleid,
          jsonfilter: this.buildJsonIn('id', this.form.selectedRecordIds),
        }]
      }
      if (this.form.ruleType === 'field') {
        let val = this.form.fieldValue
        if (this.form.fieldOper === 'in') {
          val = String(val).split(',').map(s => s.trim()).filter(Boolean)
        }
        return [{
          entityid: baseEntityId,
          objectid: this.form.objectid,
          roleid: this.form.roleid,
          jsonfilter: this.buildJsonSimple(this.form.fieldSid, this.form.fieldOper, val),
        }]
      }
      if (this.form.ruleType === 'paramentity') {
        const attr = this.catalogRefAttributes.find(a => a.entityid === this.form.paramFieldEntityId)
        if (!attr) return []
        const sourceEid = this.form.paramSourceEntityId
          || (this.refEntities.find(e => Number(e.refobjectid) === Number(attr.refobjectid))?.id)
          || attr.entityid
        return [{
          entityid: baseEntityId,
          objectid: this.form.objectid,
          roleid: this.form.roleid,
          jsonfilter: this.buildJsonParamentity(attr.sid, sourceEid),
        }]
      }
      if (this.form.ruleType === 'per_context') {
        const ctxCat = this.selectedContext
        if (!ctxCat) return []
        // Find paramentity entity for this context catalog
        const ctxParamEntity = this.refEntities.find(e => Number(e.refobjectid) === Number(ctxCat.id))
        if (!ctxParamEntity) return []
        const rules = []
        Object.entries(this.form.contextMapping).forEach(([ctxRecId, articleIds]) => {
          if (!articleIds || articleIds.length === 0) return
          rules.push({
            entityid: baseEntityId,
            objectid: this.form.objectid,
            roleid: this.form.roleid,
            jsonfilter: this.buildJsonContextMapping(ctxParamEntity.id, Number(ctxRecId), articleIds),
          })
        })
        return rules
      }
      return []
    },
    generatedJsonFilterPretty() {
      try {
        return this.generatedRules.map((r, i) => `# Правило ${i + 1}:\n${JSON.stringify(JSON.parse(r.jsonfilter), null, 2)}`).join('\n\n') || '(пусто)'
      } catch (e) {
        return this.generatedRules.map(r => r.jsonfilter).join('\n\n') || '(пусто)'
      }
    },
  },
  watch: {
    show(v) {
      if (v) this.resetFromInitial()
    },
    'form.contextCatalogId': function (newId) {
      // When user picks a context catalog, init empty arrays for each context record
      this.activeCtxId = null
      this.contextSearch = ''
      this.recordsSearch = ''
      if (!newId) return
      const ctx = this.contextCatalogs.find(c => c.id === newId)
      if (!ctx) return
      const newMapping = {}
      ctx.records.forEach(r => {
        newMapping[r.id] = this.form.contextMapping[r.id] || []
      })
      this.form.contextMapping = newMapping
      // Auto-select first subdivision for convenience
      if (ctx.records.length > 0) this.activeCtxId = ctx.records[0].id
    },
  },
  methods: {
    makeBlankForm() {
      return {
        roleid: null,
        objectid: null,
        entityid: null,
        ruleType: 'records',
        selectedRecordIds: [],
        fieldSid: '',
        fieldOper: 'equal',
        fieldValue: '',
        paramFieldEntityId: null,
        paramSourceEntityId: null,
        contextCatalogId: null,
        contextMapping: {},
      }
    },
    resetFromInitial() {
      this.stepIdx = 0
      this.aiSuggestion = null
      this.recordsSearch = ''
      this.contextSearch = ''
      this.activeCtxId = null
      this.expandedSet = new Set()
      if (this.initialRule) {
        this.editingId = this.initialRule.id
        const base = {
          ...this.makeBlankForm(),
          roleid: this.initialRule.roleid,
          objectid: this.initialRule.objectid,
          entityid: this.initialRule.entityid,
        }
        const parsed = this.reverseParseJsonFilter(this.initialRule.jsonfilter)
        this.form = { ...base, ...parsed }
      } else {
        this.editingId = null
        this.form = this.makeBlankForm()
      }
    },
    reverseParseJsonFilter(jsonStr) {
      // Best-effort: detect ruleType from existing jsonfilter (rich OR simplified format) and pre-fill form.
      if (!jsonStr) return { ruleType: 'records' }
      try {
        const obj = typeof jsonStr === 'string' ? JSON.parse(jsonStr) : jsonStr
        const root = obj?.node || obj
        const conds = root?.conds || []
        const nodes = root?.nodes || []

        // Helper: extract field sid from a left-arg (rich or simple)
        const leftSid = c => {
          const a = c.args?.[0]
          if (!a) return null
          if (a.type?.sid === 'entitysid') return a.value
          if (a.type?.sid === 'entity') return a.value?.entitysid || a.entitysid
          return null
        }
        const rightRaw = c => c.args?.[1]
        const rightValue = c => {
          const r = rightRaw(c)
          if (!r) return null
          if (r.type?.sid === 'paramentity') return { kind: 'paramentity', entityId: Number(r.entityid || r.value?.value || r.value) }
          if (r.type?.sid === 'value') {
            const v = r.value
            return { kind: 'value', value: typeof v === 'object' ? v.value : v }
          }
          if (r.type?.sid === 'formula') return { kind: 'formula', formulaId: r.value?.value }
          return { kind: 'unknown', raw: r }
        }
        // OR-group of equal conds on same field → list of ids
        const orGroupToIds = orNode => {
          if (!orNode || orNode.oper?.sid !== 'or') return null
          const conds2 = orNode.conds || []
          if (conds2.length === 0) return null
          const sids = new Set()
          const ids = []
          for (const cc of conds2) {
            if (cc.oper?.sid !== 'equal') return null
            const sid = leftSid(cc)
            const rv = rightValue(cc)
            if (!sid || !rv || rv.kind !== 'value') return null
            sids.add(sid)
            const num = Number(rv.value)
            if (Number.isFinite(num)) ids.push(num)
          }
          if (sids.size !== 1) return null
          return { fieldSid: [...sids][0], ids }
        }

        // Case A: top has nodes — usually OR-group of records list
        if (nodes.length >= 1 && conds.length === 0) {
          const idsByGroup = orGroupToIds(nodes[0])
          if (idsByGroup && idsByGroup.fieldSid === 'id') {
            return { ruleType: 'records', selectedRecordIds: idsByGroup.ids }
          }
        }

        // Case B: nodes (OR-group) + extra paramentity-cond → per_context
        if (nodes.length >= 1 && conds.length >= 1) {
          const orG = orGroupToIds(nodes[0])
          const paramCond = conds.find(c => c.args?.[0]?.type?.sid === 'paramentity')
          if (orG && paramCond && orG.fieldSid === 'id') {
            const paramEntityId = Number(paramCond.args[0].entityid || paramCond.args[0].value?.value)
            const rv = rightValue(paramCond)
            const ctxRecId = rv?.kind === 'value' ? Number(rv.value) : null
            const paramEnt = this.refEntities.find(e => e.id === paramEntityId)
            const refobjid = paramEnt && Number(paramEnt.refobjectid)
            const ctxCat = this.contextCatalogs.find(c => Number(c.id) === refobjid)
            if (ctxCat && ctxRecId) {
              return {
                ruleType: 'per_context',
                contextCatalogId: ctxCat.id,
                contextMapping: { [ctxRecId]: orG.ids },
              }
            }
          }
        }

        // Case C: single cond at top
        if (conds.length === 1 && nodes.length === 0) {
          const c = conds[0]
          const sid = leftSid(c)
          const rv = rightValue(c)
          const oper = c.oper?.sid

          if (oper === 'equal' && sid && rv?.kind === 'paramentity') {
            const attr = this.catalogRefAttributes.find(a => a.sid === sid)
            return {
              ruleType: 'paramentity',
              paramFieldEntityId: attr ? attr.entityid : null,
              paramSourceEntityId: rv.entityId || null,
            }
          }
          // legacy IN
          if (oper === 'in' && sid === 'id') {
            const arrVal = c.args?.[1]?.value
            const ids = Array.isArray(arrVal) ? arrVal.map(Number) : []
            return { ruleType: 'records', selectedRecordIds: ids }
          }
          if (sid && rv?.kind === 'value') {
            return {
              ruleType: 'field',
              fieldSid: sid,
              fieldOper: oper || 'equal',
              fieldValue: String(rv.value ?? ''),
            }
          }
        }
        return { ruleType: 'records' }
      } catch (e) {
        return { ruleType: 'records' }
      }
    },
    goStep(i) {
      if (i <= this.stepIdx) this.stepIdx = i
    },
    nextStep() { if (this.stepIdx < this.steps.length - 1) this.stepIdx += 1 },
    prevStep() { if (this.stepIdx > 0) this.stepIdx -= 1 },
    cancel() { this.$emit('close') },
    toggleRecord(id) {
      const idx = this.form.selectedRecordIds.indexOf(id)
      if (idx >= 0) this.form.selectedRecordIds.splice(idx, 1)
      else this.form.selectedRecordIds.push(id)
    },
    selectAllVisible() {
      const set = new Set(this.form.selectedRecordIds)
      this.collectVisibleLeafIds(this.filteredTreeRoots).forEach(id => set.add(id))
      this.form.selectedRecordIds = [...set]
    },
    clearSelection() { this.form.selectedRecordIds = [] },
    collectVisibleLeafIds(nodes) {
      const out = []
      const walk = n => {
        if (n.isfolder || (n.children && n.children.length > 0)) {
          (n.children || []).forEach(walk)
        } else {
          out.push(n.id)
        }
      }
      nodes.forEach(walk)
      return out
    },
    toggleExpandAll() {
      const folderIds = this.treeNodes.list.filter(n => n.isfolder || n.children.length > 0).map(n => n.id)
      if (this.allExpanded) this.expandedSet = new Set()
      else this.expandedSet = new Set(folderIds)
    },
    onTreeToggleExpand(id) {
      const s = new Set(this.expandedSet)
      if (s.has(id)) s.delete(id)
      else s.add(id)
      this.expandedSet = s
    },
    descendantLeafIds(node) {
      // Returns array of leaf ids (non-folders). If node itself is a leaf, returns [node.id].
      if (!node.isfolder && (!node.children || node.children.length === 0)) return [node.id]
      const orig = this.treeNodes.byId.get(node.id)
      if (!orig) return []
      return orig._descendantIds.slice()
    },
    onTreeToggleSelect({ node }) {
      const leafIds = this.descendantLeafIds(node)
      if (leafIds.length === 0) return
      const cur = new Set(this.form.selectedRecordIds)
      const allSelected = leafIds.every(id => cur.has(id))
      if (allSelected) leafIds.forEach(id => cur.delete(id))
      else leafIds.forEach(id => cur.add(id))
      this.form.selectedRecordIds = [...cur]
    },
    onCtxTreeToggleSelect({ node }) {
      if (!this.activeCtxId) return
      const leafIds = this.descendantLeafIds(node)
      if (leafIds.length === 0) return
      const cur = new Set(this.form.contextMapping[this.activeCtxId] || [])
      const allSelected = leafIds.every(id => cur.has(id))
      if (allSelected) leafIds.forEach(id => cur.delete(id))
      else leafIds.forEach(id => cur.add(id))
      this.$set(this.form.contextMapping, this.activeCtxId, [...cur])
    },
    getCtxMapping(ctxId) {
      return this.form.contextMapping[ctxId] || []
    },
    setCtxMapping(ctxId, selectEl) {
      const opts = Array.from(selectEl.selectedOptions || []).map(o => Number(o.value))
      this.$set(this.form.contextMapping, ctxId, opts)
    },
    pickCtx(id) {
      // eslint-disable-next-line no-console
      console.log('[RefAvail] pickCtx', id, 'prevActive=', this.activeCtxId)
      this.activeCtxId = Number(id)
      this.recordsSearch = ''
    },
    isInActiveCtx(recId) {
      if (!this.activeCtxId) return false
      return (this.form.contextMapping[this.activeCtxId] || []).indexOf(recId) >= 0
    },
    toggleActiveCtxRecord(recId) {
      if (!this.activeCtxId) return
      const arr = (this.form.contextMapping[this.activeCtxId] || []).slice()
      const idx = arr.indexOf(recId)
      if (idx >= 0) arr.splice(idx, 1)
      else arr.push(recId)
      this.$set(this.form.contextMapping, this.activeCtxId, arr)
    },
    ctxSelectAllVisible() {
      if (!this.activeCtxId) return
      const arr = (this.form.contextMapping[this.activeCtxId] || []).slice()
      const set = new Set(arr)
      this.collectVisibleLeafIds(this.filteredTreeRoots).forEach(id => set.add(id))
      this.$set(this.form.contextMapping, this.activeCtxId, [...set])
    },
    ctxClearActive() {
      if (!this.activeCtxId) return
      this.$set(this.form.contextMapping, this.activeCtxId, [])
    },
    copyFromOther() {
      if (!this.activeCtxId) return
      const filled = (this.selectedContext.records || []).filter(r => r.id !== this.activeCtxId && (this.form.contextMapping[r.id] || []).length > 0)
      if (filled.length === 0) {
        this.$bvToast && this.$bvToast.toast('Нет других заполненных подразделений', { variant: 'warning', solid: true })
        return
      }
      const choices = filled.map((r, i) => `${i + 1}. ${r.name} (${(this.form.contextMapping[r.id] || []).length} записей)`).join('\n')
      const ans = window.prompt(`Скопировать выбор из:\n${choices}\n\nВведите номер:`)
      if (!ans) return
      const idx = parseInt(ans, 10) - 1
      if (!Number.isFinite(idx) || idx < 0 || idx >= filled.length) return
      const src = filled[idx]
      const srcArr = (this.form.contextMapping[src.id] || []).slice()
      this.$set(this.form.contextMapping, this.activeCtxId, srcArr)
    },

    // ─── Rich JSON format builders ──────────────────
    // The backend FilterParser requires entity-args in this enriched shape (with attributeid,
    // parententityid, datatype). Simplified `entitysid`/`in` forms are silently ignored.
    selfEntityIdForCatalog() {
      // Catalog's "self" entity = entity whose refobjectid = catalog.id (the typed reference to this catalog)
      if (!this.catalog || !this.catalog.id) return null
      const e = (this.refEntities || []).find(x => Number(x.refobjectid) === Number(this.catalog.id) && x.sid)
      return e ? e.id : null
    },
    attributeForField(fieldSid) {
      return (this.catalogAttrs || []).find(a => a.sid === fieldSid) || null
    },
    leftEntityArg(fieldSid) {
      // LEFT side: an Entity arg pointing to a catalog attribute (e.g. 'id', 'parent', 'unitmr', ...).
      const attr = this.attributeForField(fieldSid)
      const selfEid = this.selfEntityIdForCatalog()
      return {
        type: { id: 1, sid: 'entity', name: 'Entity' },
        datatype: null,
        entityid: null,
        value: {
          title: (attr && attr.name) || fieldSid,
          value: attr ? attr.entityid : null,
          datatype: (attr && attr.datatype) || 'id',
          attributeid: attr ? attr.attributeid : null,
          entitysid: fieldSid,
          parententityid: selfEid,
          refobjectid: (attr && attr.refobjectid) || 0,
        },
        entitysid: null,
        parententityid: null,
      }
    },
    rightValueArg(fieldSid, rawValue) {
      const attr = this.attributeForField(fieldSid)
      const selfEid = this.selfEntityIdForCatalog()
      return {
        type: { id: 2, sid: 'value', name: 'Value' },
        datatype: (attr && attr.datatype) || 'id',
        entityid: attr ? attr.entityid : 1,
        value: String(rawValue),
        entitysid: fieldSid,
        parententityid: selfEid,
      }
    },
    rightParamentityArg(paramentityEntityId) {
      const ent = (this.refEntities || []).find(e => e.id === Number(paramentityEntityId))
      const selfEid = this.selfEntityIdForCatalog()
      return {
        type: { id: 7, sid: 'paramentity', name: 'Параметр' },
        datatype: 'id',
        entityid: paramentityEntityId,
        value: { value: paramentityEntityId, title: (ent && ent.name) || '', sid: (ent && ent.sid) || '' },
        entitysid: (ent && ent.sid) || '',
        parententityid: selfEid,
      }
    },
    eqCond(name, leftArg, rightArg) {
      return {
        isactive: true,
        name,
        oper: {
          id: 1, sid: 'equal', name: '=', args: 2,
        },
        args: [leftArg, rightArg],
      }
    },
    orGroupOfIds(groupName, fieldSid, ids) {
      // OR-tree: (id = N1) OR (id = N2) OR ...
      return {
        isactive: true,
        isexpanded: true,
        name: groupName,
        oper: { id: 2, sid: 'or', name: 'OR' },
        nodes: [],
        conds: ids.map((rid, i) => this.eqCond(`cond ${i + 1}`, this.leftEntityArg(fieldSid), this.rightValueArg(fieldSid, rid))),
      }
    },
    wrapTopLevel(node) {
      return {
        node: {
          isactive: true,
          oper: { id: 1, sid: 'and', name: 'And' },
          nodes: node.nodes || [],
          conds: node.conds || [],
          isexpanded: true,
        },
      }
    },

    buildJsonIn(fieldSid, ids) {
      const orNode = this.orGroupOfIds('Допустимые записи', fieldSid, ids)
      return JSON.stringify(this.wrapTopLevel({ nodes: [orNode], conds: [] }))
    },
    buildJsonSimple(fieldSid, oper, value) {
      // 'in' → OR-tree of equals; otherwise a single eq/notequal/like cond.
      if (oper === 'in') {
        const ids = Array.isArray(value) ? value : String(value).split(',').map(s => s.trim()).filter(Boolean)
        const orNode = this.orGroupOfIds(`${fieldSid} in list`, fieldSid, ids)
        return JSON.stringify(this.wrapTopLevel({ nodes: [orNode], conds: [] }))
      }
      const operMap = {
        equal: {
          id: 1, sid: 'equal', name: '=', args: 2,
        },
        notequal: {
          id: 4, sid: 'notequal', name: '<>', args: 2,
        },
        like: {
          id: 12, sid: 'like', name: 'like', args: 2,
        },
      }
      const opMeta = operMap[oper] || operMap.equal
      const cond = {
        isactive: true,
        name: 'cond 1',
        oper: opMeta,
        args: [this.leftEntityArg(fieldSid), this.rightValueArg(fieldSid, value)],
      }
      return JSON.stringify(this.wrapTopLevel({ nodes: [], conds: [cond] }))
    },
    buildJsonParamentity(fieldSid, paramentityEntityId) {
      const cond = this.eqCond('cond 1', this.leftEntityArg(fieldSid), this.rightParamentityArg(paramentityEntityId))
      return JSON.stringify(this.wrapTopLevel({ nodes: [], conds: [cond] }))
    },
    buildJsonContextMapping(paramentityEntityId, ctxRecordId, articleIds) {
      // Filter: (formula(user_subdivision) = ctxRecordId) AND (id IN articleIds)
      // Uses formula instead of paramentity — formula auto-resolves to user's value regardless of which form is open.
      // For domain 5 the "user subdivision" formula has id=190 (sid d27e1968-...). Falls back to paramentity if not provided.
      const formulaId = this.userContextFormulaId || 190
      const formulaSid = 'd27e1968-eaf6-4ba4-ab00-56666f1ae0b5'
      const ctxEnt = (this.refEntities || []).find(e => e.id === Number(paramentityEntityId))
      const selfEid = this.selfEntityIdForCatalog()
      const leftFormula = {
        type: { id: 6, sid: 'formula', name: 'Формула' },
        datatype: 'id',
        entityid: 1,
        value: {
          value: formulaId,
          title: 'Подразделение Пользователя',
          sid: formulaSid,
          entity: ctxEnt ? {
            id: ctxEnt.id,
            sid: ctxEnt.sid,
            name: ctxEnt.name,
            datatype: { id: 9, sid: 'ref', name: 'Ссылка' },
            refobjectid: Number(ctxEnt.refobjectid),
          } : null,
        },
        entitysid: 'id',
        parententityid: selfEid,
      }
      const rightCtx = {
        type: { id: 2, sid: 'value', name: 'Value' },
        datatype: 'id',
        entityid: 1,
        value: String(ctxRecordId),
        entitysid: 'id',
        parententityid: selfEid,
      }
      const ctxCond = {
        isactive: true,
        name: 'context match',
        oper: {
          id: 1, sid: 'equal', name: '=', args: 2,
        },
        args: [leftFormula, rightCtx],
      }
      const orNode = this.orGroupOfIds(`Записи для контекста ${ctxRecordId}`, 'id', articleIds)
      return JSON.stringify(this.wrapTopLevel({ nodes: [orNode], conds: [ctxCond] }))
    },

    // ─── AI helpers ──────────────────────
    async aiFindRecords() {
      const desc = window.prompt('Опишите какие записи найти (например: "статьи с кодом начинающимся на 02" или "маркетинговые статьи"):')
      if (!desc || !desc.trim()) return
      this.aiBusy = true
      try {
        const result = await this.callAi({
          task: 'find_records',
          description: desc.trim(),
          records: this.catalogRecords.map(r => ({ id: r.id, name: r.name, code: r.code })),
        })
        const ids = Array.isArray(result.ids) ? result.ids.filter(Number.isFinite) : []
        if (ids.length === 0) {
          this.aiSuggestion = { text: 'AI не нашёл подходящих записей. Уточните описание.', ids: [] }
        } else {
          this.aiSuggestion = { text: `AI нашёл ${ids.length} записей. Применить выбор?`, ids }
        }
      } catch (e) {
        this.aiSuggestion = { text: `Ошибка AI: ${e.message || e}`, ids: [] }
      }
      this.aiBusy = false
    },
    async aiSuggestDistribution() {
      if (!this.form.contextCatalogId) return
      const desc = window.prompt('Как распределить? Например: "по префиксу кода — 02 для подразделения А, 05 для Б" или "сам реши":', 'сам реши')
      if (desc === null) return
      this.aiBusy = true
      try {
        const result = await this.callAi({
          task: 'suggest_distribution',
          description: desc.trim() || 'сам реши',
          context_records: this.selectedContext.records,
          catalog_records: this.catalogRecords.map(r => ({ id: r.id, name: r.name, code: r.code })),
        })
        const mapping = result.mapping || {}
        const cnt = Object.keys(mapping).length
        this.aiSuggestion = {
          text: cnt > 0 ? `AI распределил ${cnt} контекстов. Применить?` : 'AI не предложил распределения.',
          mapping,
        }
      } catch (e) {
        this.aiSuggestion = { text: `Ошибка AI: ${e.message || e}`, mapping: {} }
      }
      this.aiBusy = false
    },
    applyAiSuggestion() {
      if (!this.aiSuggestion) return
      if (Array.isArray(this.aiSuggestion.ids)) {
        const set = new Set(this.form.selectedRecordIds)
        this.aiSuggestion.ids.forEach(id => set.add(id))
        this.form.selectedRecordIds = [...set]
      }
      if (this.aiSuggestion.mapping) {
        Object.entries(this.aiSuggestion.mapping).forEach(([k, v]) => {
          if (Array.isArray(v) && v.length > 0) {
            this.$set(this.form.contextMapping, k, v)
          }
        })
      }
      this.aiSuggestion = null
    },

    async callAi(payload) {
      // Send a focused request to AI via /ws/chat — short prompt, structured response
      const base = getBaseURL()
      const url = new URL('/ws/chat', base)
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
      const ws = new WebSocket(url.toString())
      const token = getSessionToken() || ''
      let resolveFn
      let rejectFn
      const promise = new Promise((res, rej) => { resolveFn = res; rejectFn = rej })
      let combined = ''
      let timer = null
      const finish = () => {
        try { ws.close() } catch (e) { /* */ }
        try {
          const m = combined.match(/```json\s*([\s\S]*?)```/)
          const obj = m ? JSON.parse(m[1].trim()) : JSON.parse(combined.trim())
          resolveFn(obj)
        } catch (e) { rejectFn(new Error(`Не удалось распарсить ответ AI: ${e.message}`)) }
      }
      ws.onopen = () => {
        ws.send(JSON.stringify({ type: 'auth', token }))
        const prompt = this.buildAiPrompt(payload)
        ws.send(JSON.stringify({
          type: 'message', to: 'chatbot', text: prompt, useAi: true, token,
        }))
      }
      ws.onmessage = event => {
        try {
          const data = JSON.parse(event.data)
          if (data.type === 'message') {
            combined += data.text || ''
            if (timer) clearTimeout(timer)
            // Wait 1s of silence to consider response complete
            timer = setTimeout(finish, 1000)
          }
        } catch (e) { /* ignore */ }
      }
      ws.onerror = () => rejectFn(new Error('WebSocket error'))
      // Hard timeout 30s
      setTimeout(() => { try { ws.close() } catch (e) { /* */ } if (!combined) rejectFn(new Error('Timeout')) }, 30000)
      return promise
    },
    buildAiPrompt(payload) {
      if (payload.task === 'find_records') {
        return `Помоги найти записи каталога по описанию.
Описание: "${payload.description}"
Записи каталога (id, name, code):
${JSON.stringify(payload.records, null, 0)}

Верни JSON: {"ids":[<массив id подходящих записей>]} в блоке \`\`\`json...\`\`\`.
Без объяснений, только JSON. Если ничего не подходит — пустой массив.`
      }
      if (payload.task === 'suggest_distribution') {
        return `Помоги распределить записи каталога между контекстами.
Описание стратегии: "${payload.description}"
Контексты (id, name): ${JSON.stringify(payload.context_records, null, 0)}
Записи каталога (id, name, code): ${JSON.stringify(payload.catalog_records, null, 0)}

Верни JSON: {"mapping": {"<context_id>": [<массив id_записей>]}} в блоке \`\`\`json...\`\`\`.
Только JSON без объяснений.`
      }
      return JSON.stringify(payload)
    },

    // ─── Save ────────────────────────────
    async save() {
      this.saving = true
      try {
        const rules = this.generatedRules
        if (rules.length === 0) {
          this.$bvToast.toast('Нет данных для сохранения', { title: 'Ошибка', variant: 'danger', solid: true })
          this.saving = false
          return
        }
        for (const rule of rules) {
          if (this.editingId && rules.length === 1) {
            // Update existing
            this.$emit('save', { type: 'update', id: this.editingId, rule })
          } else {
            this.$emit('save', { type: 'create', rule })
          }
          // small delay to avoid race
          await new Promise(r => setTimeout(r, 200))
        }
        this.$emit('saved')
        this.$emit('close')
      } catch (e) {
        this.$bvToast.toast(`Ошибка: ${e.message || e}`, { title: 'Ошибка', variant: 'danger', solid: true })
      }
      this.saving = false
    },
  },
}
</script>

<style scoped>
.raw-wrap { display: flex; flex-direction: column; min-height: 500px; }
.raw-steps { display: flex; gap: 4px; margin-bottom: 16px; padding: 4px; background: #f5f5f5; border-radius: 6px; }
.raw-step { flex: 1; display: flex; align-items: center; gap: 6px; padding: 8px 12px; cursor: pointer; border-radius: 4px; font-size: 12px; color: #888; }
.raw-step--active { background: #7367f0; color: #fff; font-weight: 600; }
.raw-step--done { color: #4caf50; }
.raw-step--done:not(.raw-step--active):hover { background: #e8f5e9; }
.raw-step-num { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; background: rgba(255,255,255,0.3); border-radius: 50%; font-weight: 600; }
.raw-step--active .raw-step-num { background: rgba(255,255,255,0.4); }
.raw-step--done:not(.raw-step--active) .raw-step-num { background: #c8e6c9; color: #2e7d32; }

.raw-step-body { flex: 1; padding: 8px; min-height: 360px; }
.raw-step-body h5 { margin: 0 0 12px; color: #333; font-size: 14px; font-weight: 600; }
.raw-row { margin-bottom: 12px; }
.raw-row label { display: block; font-size: 12px; color: #555; margin-bottom: 4px; font-weight: 600; }
.raw-input { width: 100%; padding: 6px 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 13px; }
.raw-hint { font-size: 11px; color: #888; margin-top: 3px; }
.raw-warn { padding: 6px 10px; background: #fff3e0; border: 1px solid #ffcc80; border-radius: 4px; font-size: 12px; color: #e65100; margin-top: 6px; }
.raw-warn-inline { color: #e65100; font-size: 11px; margin-left: 8px; }
.raw-req { color: #e74c3c; }

.raw-types { display: flex; flex-direction: column; gap: 8px; }
.raw-type { display: flex; align-items: flex-start; gap: 10px; padding: 12px; border: 1px solid #ddd; border-radius: 6px; cursor: pointer; }
.raw-type:hover:not(.raw-type--disabled) { border-color: #7367f0; background: #f8f7ff; }
.raw-type--active { border-color: #7367f0; background: #e8e5ff; }
.raw-type--disabled { opacity: 0.5; cursor: not-allowed; }
.raw-type input { margin-top: 3px; }
.raw-type strong { font-size: 13px; }
.raw-type-hint { font-size: 11px; color: #777; margin-top: 2px; }

.raw-toolbar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.raw-link { background: none; border: none; color: #7367f0; cursor: pointer; font-size: 12px; padding: 0; }
.raw-link:hover { text-decoration: underline; }
.raw-link--secondary { color: #999; }
.raw-counter { font-size: 12px; color: #555; font-weight: 600; }
.raw-counter-small { font-size: 10px; color: #999; }
.raw-ai-btn { padding: 4px 10px; background: #f3e5f5; color: #6a1b9a; border: 1px solid #e1bee7; border-radius: 4px; font-size: 12px; cursor: pointer; margin-left: auto; }
.raw-ai-btn:hover:not(:disabled) { background: #e1bee7; }
.raw-ai-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.raw-ai-suggestion { padding: 8px 12px; background: #fff8e1; border: 1px solid #ffe082; border-radius: 4px; margin-bottom: 8px; display: flex; align-items: center; gap: 10px; font-size: 12px; }
.raw-ai-suggestion span { flex: 1; }

.raw-records-list { max-height: 360px; overflow-y: auto; border: 1px solid #e5e7ee; border-radius: 8px; background: #fff; padding: 4px 0; }
.raw-rec-item { display: flex; align-items: center; gap: 8px; padding: 5px 12px; border-bottom: 1px solid #f5f5f5; cursor: pointer; font-size: 12px; }
.raw-rec-item:hover { background: #fafbff; }
.raw-rec-item--checked { background: #e8e5ff; }
.raw-rec-code { font-family: monospace; color: #888; min-width: 70px; font-size: 11px; }
.raw-rec-name { flex: 1; }

/* ─── Tree (Radix/Shadcn style) ───────────────────── */
.raw-tree-node { display: block; }
.raw-tree-children { display: block; }

.raw-tree-row {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 8px;
  margin: 1px 4px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: #18181b;
  transition: background-color 0.12s ease;
  user-select: none;
}
.raw-tree-row:hover { background: #f4f4f5; }
.raw-tree-row--checked { background: #ede9fe; }
.raw-tree-row--checked:hover { background: #ddd6fe; }
.raw-tree-row--partial { background: #faf5ff; }
.raw-tree-row--partial:hover { background: #f3e8ff; }
.raw-tree-row--folder { font-weight: 500; color: #09090b; }

.raw-tree-indent { display: inline-block; flex: 0 0 auto; }

/* Chevron — SVG, rotates 90deg on expand */
.raw-tree-chev {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px; height: 18px;
  flex: 0 0 auto;
  color: #71717a;
  cursor: pointer;
  border-radius: 4px;
  transition: transform 0.15s ease, color 0.12s ease, background-color 0.12s ease;
}
.raw-tree-chev:not(.raw-tree-chev--empty):hover { background: #e4e4e7; color: #18181b; }
.raw-tree-chev--open { transform: rotate(90deg); color: #18181b; }
.raw-tree-chev--empty { cursor: default; visibility: hidden; }

/* Checkbox */
.raw-tree-cbx {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 16px; height: 16px;
  flex: 0 0 auto;
  cursor: pointer;
}
.raw-tree-cbx input {
  position: absolute; opacity: 0; width: 100%; height: 100%; margin: 0; cursor: pointer; z-index: 1;
}
.raw-tree-cbx-mark {
  width: 16px; height: 16px;
  border: 1.5px solid #d4d4d8;
  border-radius: 4px;
  background: #fff;
  transition: all 0.12s ease;
  position: relative;
}
.raw-tree-cbx:hover .raw-tree-cbx-mark { border-color: #7c3aed; }
.raw-tree-cbx input:checked + .raw-tree-cbx-mark,
.raw-tree-cbx input:indeterminate + .raw-tree-cbx-mark {
  background: #7c3aed;
  border-color: #7c3aed;
}
/* Checkmark */
.raw-tree-cbx input:checked + .raw-tree-cbx-mark::after {
  content: '';
  position: absolute;
  left: 5px; top: 1.5px;
  width: 4px; height: 8px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
/* Minus icon for indeterminate */
.raw-tree-cbx input:indeterminate + .raw-tree-cbx-mark::after {
  content: '';
  position: absolute;
  left: 3px; top: 6px;
  width: 8px; height: 2px;
  background: #fff;
  border-radius: 1px;
}

/* Code — subtle mono chip */
.raw-tree-code {
  font-family: ui-monospace, Menlo, Consolas, monospace;
  color: #a1a1aa;
  font-size: 11.5px;
  min-width: 52px;
  max-width: 96px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 0 0 auto;
}

/* Name */
.raw-tree-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.3;
}

/* Counter badge */
.raw-tree-badge {
  font-size: 11px;
  padding: 0 8px;
  height: 18px;
  line-height: 18px;
  border-radius: 9px;
  background: #f4f4f5;
  color: #71717a;
  font-weight: 500;
  flex: 0 0 auto;
  font-variant-numeric: tabular-nums;
  margin-left: auto;
}
.raw-tree-badge--partial { background: #fef3c7; color: #92400e; }
.raw-tree-badge--full { background: #dcfce7; color: #166534; }

/* ─── Inline picker wrapper (with clear-X) ─────── */
.raw-picker-wrap {
  display: flex; align-items: center; gap: 6px;
  padding: 4px 0;
}
.raw-picker-clear {
  display: inline-flex; align-items: center; justify-content: center;
  width: 18px; height: 18px;
  border: none; background: transparent;
  color: #b9b9c3; cursor: pointer;
  border-radius: 50%; font-size: 16px; line-height: 1;
  transition: all 0.12s ease;
}
.raw-picker-clear:hover { background: #f0eef9; color: #ea5455; }
/* Make inline-picker wider in our form rows (penetrate scoped style) */
.raw-row >>> .cbs-step-picker { max-width: 100% !important; flex: 1; }
.raw-row >>> .cbs-step-picker__panel { width: 100% !important; min-width: 280px; }
.raw-row >>> .cbs-step-picker__current {
  display: inline-block;
  padding: 6px 10px;
  border: 1px solid #d8d6de;
  border-radius: 4px;
  background: #fff;
  width: 100%;
  text-decoration: none !important;
  font-size: 13px;
}
.raw-row >>> .cbs-step-picker__current:hover { text-decoration: none !important; border-color: #7367f0; }

/* Container — clean card, no inner borders */
.raw-records-list {
  max-height: 360px;
  overflow-y: auto;
  border: 1px solid #e4e4e7;
  border-radius: 8px;
  background: #fff;
  padding: 4px 0;
}

.raw-context-mapping { margin-top: 8px; }
.raw-input--small { padding: 4px 8px; font-size: 12px; margin-bottom: 6px; }

.raw-md-layout { display: grid; grid-template-columns: 280px 1fr; gap: 12px; min-height: 380px; }
.raw-md-master { display: flex; flex-direction: column; border: 1px solid #eee; border-radius: 6px; padding: 8px; background: #fafafa; }
.raw-md-list { flex: 1; overflow-y: auto; max-height: 360px; }
.raw-md-item { display: flex; align-items: center; justify-content: space-between; padding: 6px 10px; cursor: pointer; border-radius: 4px; font-size: 12px; margin-bottom: 2px; }
.raw-md-item:hover { background: #f0eeff; }
.raw-md-item--active { background: #7367f0; color: #fff; font-weight: 600; }
.raw-md-item--active:hover { background: #7367f0; }
.raw-md-item--filled .raw-md-name::before { content: '● '; color: #4caf50; }
.raw-md-item--active.raw-md-item--filled .raw-md-name::before { color: #c8e6c9; }
.raw-md-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.raw-md-badge { padding: 1px 8px; background: rgba(115,103,240,0.15); color: #5e51d6; border-radius: 10px; font-size: 11px; font-weight: 600; }
.raw-md-item--active .raw-md-badge { background: rgba(255,255,255,0.3); color: #fff; }

.raw-md-detail { display: flex; flex-direction: column; border: 1px solid #eee; border-radius: 6px; padding: 8px; background: #fff; }
.raw-md-detail-head { display: flex; justify-content: space-between; align-items: center; padding: 4px 0 8px; border-bottom: 1px solid #f0f0f0; margin-bottom: 8px; font-size: 13px; }
.raw-md-placeholder { text-align: center; padding: 60px 20px; color: #999; font-size: 13px; }
.raw-records-list--md { max-height: 280px; }

.raw-preview { padding: 12px; background: #fafafa; border: 1px solid #eee; border-radius: 4px; font-size: 13px; }
.raw-preview > div { margin-bottom: 6px; }
.raw-json-details { margin-top: 12px; }
.raw-json-details summary { cursor: pointer; font-size: 12px; color: #888; }
.raw-json { padding: 10px; background: #f5f5f5; border-radius: 4px; font-size: 11px; max-height: 200px; overflow-y: auto; margin-top: 6px; }
.raw-info { padding: 8px 12px; background: #e3f2fd; border: 1px solid #90caf9; border-radius: 4px; font-size: 12px; color: #0277bd; margin-top: 8px; }

.raw-footer { margin-top: 16px; padding-top: 12px; border-top: 1px solid #eee; display: flex; justify-content: flex-end; gap: 8px; }
.raw-btn { padding: 6px 14px; border: 1px solid #ddd; background: #fff; border-radius: 4px; font-size: 13px; cursor: pointer; }
.raw-btn:hover { background: #f5f5f5; }
.raw-btn--primary { background: #7367f0; color: #fff; border-color: #7367f0; }
.raw-btn--primary:hover { background: #5e51d6; }
.raw-btn--primary:disabled { background: #b0a8e8; cursor: not-allowed; }

.raw-empty { text-align: center; padding: 20px; color: #999; }
</style>
